import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { IBaseComponent } from '~/sharedInterfaces/IBaseComponent';
import { IState } from '~/sharedInterfaces/IState';
import { IBaseInput } from '~/sharedInterfaces/SharedInterfaces';
import { getCurrency, getInputClassNames } from '~/utils';
import { changePrice } from './LoanCalculatorService';
import { getDefaultLabelName } from '~/services/DefaultLabelNameService';
import Tooltip from '../Base/Tooltip/Tooltip';

export interface ILoanCalculator extends IBaseInput {
  monthlyFee?: string;
  monthltyAmortisation?: string;
  monthlyTotal?: string;
  disclaimer?: string;
}

interface ILoanCalculatorProps extends ILoanCalculator, IBaseComponent {}

const LoanCalculator: FC<ILoanCalculatorProps> = (props) => {
  const { loanAmount, loanTerm } = useSelector((state: IState) => state.signup);
  const { locale } = useSelector((state: IState) => state.widgetContext);
  const { market } = props.settings;
  const currency = getCurrency(market);

  const {
    monthlyFee,
    monthltyAmortisation,
    monthlyTotal,
    disclaimer,
    tooltipText,
  } = getDefaultLabelName(locale, props.componentName);

  const [calculations, setCalculations] = useState({
    fee: '0',
    amortisation: '0',
    total: '0',
  });

  useEffect(() => {
    if (loanAmount && loanTerm) {
      setCalculations(changePrice(loanAmount, loanTerm, market));
    }
  }, [loanAmount, loanTerm]);

  return (
    <div
      className={getInputClassNames({
        tooltip: props.tooltip,
        componentName: 'loancalculator',
      })}
    >
      {tooltipText && <Tooltip tooltip={props.tooltip} tooltipText={tooltipText} />}
      <p className="qred-first">
        <span className="qred-first">{props.monthlyFee || monthlyFee}</span>
        {currency === '€' && <span className="qred-second">{currency}</span>}
        <span
          data-testid="loan-calculator-fee"
          className={currency === '€' ? 'qred-third' : 'qred-second'}
        >
          {calculations.fee}
        </span>
        {currency !== '€' && <span className="qred-third">{currency}</span>}
      </p>
      <p className="qred-second">
        <span className="qred-first">{props.monthltyAmortisation || monthltyAmortisation}</span>
        {currency === '€' && <span className="qred-second">{currency}</span>}
        <span
          data-testid="loan-calculator-amortisation"
          className={currency === '€' ? 'qred-third' : 'qred-second'}
        >
          {calculations.amortisation}
        </span>
        {currency !== '€' && <span className="qred-third">{currency}</span>}
      </p>
      <p className="qred-third">
        <span className="qred-first">{props.monthlyTotal || monthlyTotal}</span>
        {currency === '€' && <span className="qred-second">{currency}</span>}
        <span
          data-testid="loan-calculator-total"
          className={currency === '€' ? 'qred-third' : 'qred-second'}
        >
          {calculations.total}
        </span>
        {currency !== '€' && <span className="qred-third">{currency}</span>}
        <span className="qred-fourth">*</span>
      </p>
      <p className="qred-fourth">
        <span className="qred-first">*</span>
        <span className="qred-second">{props.disclaimer || disclaimer}</span>
      </p>
    </div>
  );
};

export default LoanCalculator;
